import React from 'react';

import useIsMinBreakpoint from '@hero/react-hooks/mediaQuery/useIsMinBreakpoint';
import MembersSparklingIllustration from '@hero/ui-kit/graphics/Illustrations/MembersSparklingIllustration';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import LimitItems from '../../../components/LimitItems';
import Error from '../../../components/States/Error';
import NoResults from '../../../components/States/NoResults';
import Table from '../../../components/Table';
import MembersToolbar from '../OrganizationMemberList/MembersToolbar';
import { useUserStatusContext } from '../../../context/UserStatus';
import useColumnVisibility from '../../../hooks/useColumnVisibility';
import { buildColumnOptions, buildGridTemplate } from '../../../utils/tableColumns';
import buildHeaderColumns from '../../../components/SortBy/buildHeaderColumns';
import useListQueryParams from '../OrganizationMemberList/useListQueryParams';
import { memberSortDefaultDir } from '../OrganizationMemberList/utils/memberSort';
import { OrganizationMemberListPaginated } from '../types';
import { DISCHARGED_COLUMNS, DISCHARGED_COLUMNS_STORAGE_KEY, DischargedColumnId } from './columns';
import { MISMATCH_ALERT_TRACK } from '../components/MemberMismatchAlert';
import ListItem from './ListItem';

const DischargedMemberList: React.FC<{
    isLoading: boolean;
    isError: boolean;
    data?: OrganizationMemberListPaginated;
}> = ({ isLoading, isError, data }) => {
    const isMinL = useIsMinBreakpoint('l');
    const { permissions } = useUserStatusContext();

    const { query } = useListQueryParams();
    const { limit, offset } = query;
    const search = query?.search || '';
    const hasFilters = !!query.device_status || !!query.is_online;

    const { isVisible, toggle } = useColumnVisibility(
        DISCHARGED_COLUMNS_STORAGE_KEY,
        DISCHARGED_COLUMNS
    );

    const gridTemplate = buildGridTemplate(DISCHARGED_COLUMNS, isVisible, {
        trailingTracks: [MISMATCH_ALERT_TRACK],
        collapsed: !isMinL
    });
    const headerColumns = buildHeaderColumns(DISCHARGED_COLUMNS, isVisible, memberSortDefaultDir);
    const columnControl = {
        options: buildColumnOptions(DISCHARGED_COLUMNS, isVisible),
        onToggle: (id: string) => toggle(id as DischargedColumnId)
    };

    if (isError) {
        return <Error />;
    }

    return (
        <>
            <MembersToolbar>
                <LimitItems
                    limit={limit ? +limit : 10}
                    offset={offset ? +offset : 0}
                    total_count={data?.total_count}
                    isLoading={isLoading}
                    columnControl={columnControl}
                />
            </MembersToolbar>

            {data?.list.length === 0 && search.length === 0 && !hasFilters ? (
                <Section centered>
                    <H role="h6" styles={{ fontSize: '24px' }}>
                        No discharged members
                    </H>
                    <MembersSparklingIllustration
                        styles={{ height: '20rem', width: '64rem', marginTop: '5rem' }}
                    />
                </Section>
            ) : null}

            {data?.list.length === 0 && search.length ? <NoResults search={search} /> : null}

            {data?.list.length ? (
                <Table
                    pagerData={data}
                    headerColumns={headerColumns}
                    headerGridTemplateColumns={gridTemplate}
                    loading={isLoading}
                    gridColumnGap="small"
                >
                    {data?.list.map((item, index) => (
                        <ListItem
                            statusInfo={item.status_info}
                            memberId={item.member_id}
                            heroUserId={item.hero_user_id}
                            key={`${index}-${item.hero_user_id || item.member_id}-${
                                item.email || 'n/a'
                            }`}
                            name={
                                item.first_name || item.last_name
                                    ? `${item.first_name} ${item.last_name}`
                                    : 'N/A'
                            }
                            firstName={item.first_name}
                            lastName={item.last_name}
                            dateOfBirth={item.date_of_birth}
                            deviceFirstName={item.fre_first_name}
                            deviceLastName={item.fre_last_name}
                            deviceDateOfBirth={item.fre_dob}
                            email={item.email || undefined}
                            search={search}
                            canOpenDetails={permissions.fleet.fleetMemberView}
                            readmittedTo={item.readmitted_to}
                            careManagerIds={item.care_manager_ids || null}
                            externalUserId={item.external_user_id}
                            activatedAt={item.activated_at}
                            deviceInfo={item.device_info || undefined}
                            exchangeDeviceInfo={item.exchange_device_info || undefined}
                            role={item.member_role}
                            listQuery={query}
                            isColumnVisible={isVisible}
                            gridColumns={gridTemplate}
                        />
                    ))}
                </Table>
            ) : null}
        </>
    );
};

export default DischargedMemberList;
